var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import { isArrowLeft, isArrowRight, isArrowDown } from '../../util/key-codes';
import TabList from './components/TabList';
import Tab from './components/Tab';
import TabPanel from './components/TabPanel';
class Tabs extends Component {
    constructor() {
        super(...arguments);
        this.state = { selectedIndex: this.props.initialSelectedIndex };
        this.tabPanelsRefs = createRefs(this.props.items.length);
        this.handleChange = (selectedIndex) => this.setState({ selectedIndex });
        this.handleTabKeyDown = (e) => {
            const { selectedIndex } = this.state;
            const nextTab = Math.min(this.props.items.length - 1, selectedIndex + 1);
            const previousTab = Math.max(0, selectedIndex - 1);
            const panelRef = this.tabPanelsRefs[selectedIndex].current;
            if (isArrowLeft(e)) {
                this.setState({ selectedIndex: previousTab });
            }
            else if (isArrowRight(e)) {
                this.setState({ selectedIndex: nextTab });
            }
            else if (isArrowDown(e)) {
                if (panelRef) {
                    panelRef.focus();
                }
            }
        };
    }
    render() {
        const _a = this.props, { items } = _a, props = __rest(_a, ["items"]);
        const { selectedIndex } = this.state;
        const { tabs, panels } = items.reduce((aggr, { id, tab, panel }, index) => {
            const { tabId, panelId } = getIds(id);
            const tabElement = (_jsx(Tab, Object.assign({ "data-testid": "tab-element", selected: selectedIndex === index, onClick: () => this.handleChange(index), id: tabId, "aria-controls": panelId, onKeyDown: this.handleTabKeyDown }, { children: tab }), tabId));
            const tabPanelElement = (_jsx(TabPanel, Object.assign({ "data-testid": "tab-panel", id: panelId, "aria-labelledby": tabId, hidden: selectedIndex !== index, ref: this.tabPanelsRefs[index] }, { children: panel }), tabId));
            return {
                tabs: [...aggr.tabs, tabElement],
                panels: [...aggr.panels, tabPanelElement],
            };
        }, { tabs: [], panels: [] });
        return (_jsxs(Fragment, { children: [_jsx(TabList, Object.assign({}, props, { children: tabs })), panels] }));
    }
}
Tabs.propTypes = {
    /**
     * The index of the initially selected tab.
     */
    initialSelectedIndex: PropTypes.number,
    /**
     * A collection of tabs with an id, the tab label, and panel content.
     */
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        tab: PropTypes.node.isRequired,
        panel: PropTypes.node.isRequired,
    })).isRequired,
};
Tabs.defaultProps = {
    initialSelectedIndex: 0,
};
function getIds(id) {
    return {
        tabId: `tab-${id}`,
        panelId: `panel-${id}`,
    };
}
function createRefs(length) {
    return Array.from(Array(length).keys()).map(createRef);
}
export default Tabs;
